import { useEffect, useState } from "react";
import { Rules as RulesAPI } from "@/lib/api";
import { useTeam } from "@/lib/team";
import PageHeader from "@/components/PageHeader";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import { toast } from "sonner";
import { Plus, Trash, GitBranch } from "@phosphor-icons/react";

export default function RulesPage() {
  const { users } = useTeam();
  const [rules, setRules] = useState([]);
  const [open, setOpen] = useState(false);

  const load = () => RulesAPI.list().then(setRules);
  useEffect(() => { load(); }, []);

  const remove = async (id) => {
    if (!confirm("Delete rule?")) return;
    await RulesAPI.remove(id);
    load();
  };

  const userById = (id) => users.find(u => u.id === id);

  return (
    <div data-testid="rules-page">
      <PageHeader
        overline="Automation · Routing"
        title="Assignment Rules"
        description="Auto-route new prospects to a specific teammate based on industry, city, source, or tags. Lower priority number = evaluated first."
        testId="rules-header"
        actions={
          <Button onClick={() => setOpen(true)} className="bg-neutral-900 hover:bg-black text-white" data-testid="new-rule-btn">
            <Plus size={16} className="mr-2" /> New Rule
          </Button>
        }
      />
      <div className="p-10 space-y-4">
        {rules.length === 0 && (
          <div className="text-center py-20 text-sm text-neutral-500">
            No rules yet. Create one to auto-route new prospects to teammates.
          </div>
        )}
        <div className="stagger-in space-y-3">
          {rules.map(r => {
            const owner = userById(r.assign_to);
            return (
              <Card key={r.id} className="p-5 rounded-md border-neutral-200 flex items-center justify-between" data-testid={`rule-${r.id}`}>
                <div className="flex items-center gap-4">
                  <div className="overline w-8">#{r.priority}</div>
                  <GitBranch size={18} weight="duotone" className="text-neutral-500" />
                  <div>
                    <div className="text-sm font-medium">{r.name}</div>
                    <div className="text-xs text-neutral-500 font-mono mt-1">
                      IF <span className="text-neutral-900">{r.match_field}</span> {r.match_op} <span className="text-neutral-900">"{r.match_value}"</span>
                    </div>
                  </div>
                </div>
                <div className="flex items-center gap-3">
                  <div className="text-xs text-neutral-500">assign to</div>
                  {owner ? (
                    <div className="flex items-center gap-2">
                      <div className="w-6 h-6 rounded-full grid place-items-center text-white text-[9px] font-mono" style={{ background: owner.color }}>{owner.initials}</div>
                      <span className="text-sm">{owner.name}</span>
                    </div>
                  ) : <Badge variant="outline">unknown user</Badge>}
                  <Button size="sm" variant="ghost" onClick={() => remove(r.id)} data-testid={`delete-rule-${r.id}`}>
                    <Trash size={14} />
                  </Button>
                </div>
              </Card>
            );
          })}
        </div>
      </div>
      <NewRuleDialog open={open} onOpenChange={setOpen} onSaved={load} users={users} />
    </div>
  );
}

function NewRuleDialog({ open, onOpenChange, onSaved, users }) {
  const [f, setF] = useState({ name: "", priority: 100, match_field: "industry", match_op: "contains", match_value: "", assign_to: "" });
  const submit = async () => {
    if (!f.name || !f.match_value || !f.assign_to) return toast.error("Fill all fields");
    await RulesAPI.create(f);
    toast.success("Rule created");
    setF({ name: "", priority: 100, match_field: "industry", match_op: "contains", match_value: "", assign_to: "" });
    onOpenChange(false);
    onSaved();
  };
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-lg" data-testid="new-rule-dialog">
        <DialogHeader>
          <DialogTitle className="font-display text-2xl">New assignment rule</DialogTitle>
          <DialogDescription>Rules evaluate in priority order (lowest first). First match wins.</DialogDescription>
        </DialogHeader>
        <div className="space-y-4">
          <div className="grid grid-cols-3 gap-4">
            <div className="col-span-2">
              <Label>Name</Label>
              <Input value={f.name} onChange={e => setF({...f, name: e.target.value})} placeholder="Bay Area cafes → Alex" data-testid="rule-name" />
            </div>
            <div>
              <Label>Priority</Label>
              <Input type="number" value={f.priority} onChange={e => setF({...f, priority: Number(e.target.value)})} data-testid="rule-priority" />
            </div>
          </div>
          <div className="grid grid-cols-3 gap-4">
            <div>
              <Label>Field</Label>
              <Select value={f.match_field} onValueChange={v => setF({...f, match_field: v})}>
                <SelectTrigger data-testid="rule-field"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="industry">industry</SelectItem>
                  <SelectItem value="city">city</SelectItem>
                  <SelectItem value="source">source</SelectItem>
                  <SelectItem value="tags">tags</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label>Operator</Label>
              <Select value={f.match_op} onValueChange={v => setF({...f, match_op: v})}>
                <SelectTrigger data-testid="rule-op"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="contains">contains</SelectItem>
                  <SelectItem value="equals">equals</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label>Value</Label>
              <Input value={f.match_value} onChange={e => setF({...f, match_value: e.target.value})} placeholder="cafe" data-testid="rule-value" />
            </div>
          </div>
          <div>
            <Label>Assign to</Label>
            <Select value={f.assign_to} onValueChange={v => setF({...f, assign_to: v})}>
              <SelectTrigger data-testid="rule-assign-to"><SelectValue placeholder="Pick a teammate" /></SelectTrigger>
              <SelectContent>
                {users.map(u => <SelectItem key={u.id} value={u.id}>{u.name}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>Cancel</Button>
          <Button className="bg-neutral-900 hover:bg-black" onClick={submit} data-testid="save-rule-btn">Create rule</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
